<script setup lang="ts">
import { ref } from 'vue'
import WikiText from '@/components/wikitext/WikiText.vue'
import WikiTextEditor from '@/components/editor/WikiTextEditor.vue'
import type { Resource } from '@/resources/resource_types'
import type { WikiTextLockMode } from '@/components/wikitext/wiki_text_types'

const strategies = {
  'calming strategies': {
    uuid: 'sample-strategy',
    title: 'Calming Strategies',
    slug: 'calming-strategies',
    description: 'Ways for students to calm down before re-engaging with a task.',
    image_url: '',
  },
}

const resources = {
  'sample-resource': {
    uuid: 'sample-resource',
    title: 'Practice Worksheet',
    type: 'pdf',
    description: 'A printable worksheet.',
    download_links: [],
    google_drive_links: [],
    screenshots: [],
    num_pages: 2,
    language: 'English',
  } as unknown as Resource,
}

const formattingText = '## Heading\n\n- Markdown list\n- syntax\n\n**Bold**, *italic*, <b>inline HTML</b>, x<sup>2</sup>, H<sub>2</sub>O and 3 &times; 4.'

const wikiLinkText =
  'Model the skill, then [[calming strategies|use a calming strategy]]. ' +
  'Print the [[sample-resource|practice worksheet]], or read [[https://example.com|the guide]]. ' +
  'An [[unknown target|unresolved link]] renders as its label.'

const visualListText =
  '**Example Exercises:**<br>\n' +
  '• Curls using a stretch band<br>\n' +
  '• Leg lifts<br>'

const lockedText =
  'The first block of a locked strategy stays readable in default mode.\n\n' +
  'Every later block is blurred, and [[https://example.com|links]] are not reachable.'

const originalNotes = 'Start with [[https://example.com|the guide]], then practise twice.<br>\nCheck in on Friday.'
const notes = ref(originalNotes)

const lockModes: WikiTextLockMode[] = ['default', 'always_snippet_first_block', 'lock_links', 'completely_blur']
</script>

<template>
  <div class="grid grid-cols-2 gap-8">
    <section>
      <h3 class="header-sm mb-2">Markdown and inline HTML</h3>
      <WikiText :text="formattingText" />
    </section>
    <section>
      <h3 class="header-sm mb-2">Wiki links</h3>
      <WikiText :text="wikiLinkText" :strategies="strategies" :resources="resources" />
    </section>
    <section>
      <h3 class="header-sm mb-2">Bullet-glyph content, rebuilt as a list</h3>
      <WikiText :text="visualListText" />
    </section>
    <section>
      <h3 class="header-sm mb-2">Plaintext</h3>
      <WikiText :text="wikiLinkText" :strategies="strategies" :resources="resources" plaintext />
    </section>
    <section>
      <h3 class="header-sm mb-2">Editable</h3>
      <WikiTextEditor v-model:wikiText="notes" format="markdown" label="Sample notes" :reset-value="originalNotes" show-reset place-holder="Add your notes" />
    </section>
    <section>
      <h3 class="header-sm mb-2">Editable, read-only</h3>
      <WikiTextEditor :wiki-text="originalNotes" format="markdown" label="Sample read-only notes" read-only read-only-hint="To make edits, copy this page to your own Library." />
    </section>
    <section v-for="lockMode in lockModes" :key="lockMode">
      <h3 class="header-sm mb-2">Locked: {{ lockMode }}</h3>
      <WikiText :text="lockedText" locked :lock-mode="lockMode" />
    </section>
  </div>
</template>
